/**
 * 미디어 쿼리 감지 커스텀 훅
 * 브라우저의 화면 크기 변화를 감지하여 반응형 UI를 구현할 때 사용합니다.
 *
 * @param {string} query - 미디어 쿼리 문자열 (예: '(max-width: 768px)')
 * @returns {boolean} - 현재 미디어 쿼리 조건이 맞으면 true, 아니면 false
 *
 * @example
 * const isMobile = useMediaQuery('(max-width: 768px)');
 * const isDesktop = useMediaQuery('(min-width: 1024px)');
 *
 * // 테마와 함께 사용
 * import theme from '../styles/theme';
 * const isMobile = useMediaQuery(theme.media.maxMobile);
 */

import { useState, useEffect } from 'react';

export const useMediaQuery = (query) => {
  // SSR 대응: window가 없는 환경에서는 false 반환
  const [matches, setMatches] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.matchMedia(query).matches;
    }
    return false;
  });

  useEffect(() => {
    // window가 없는 환경에서는 실행하지 않음
    if (typeof window === 'undefined') {
      return;
    }

    const mediaQuery = window.matchMedia(query);

    // 초기 상태 설정
    setMatches(mediaQuery.matches);

    // 미디어 쿼리 변경 감지 핸들러
    const handleChange = (event) => {
      setMatches(event.matches);
    };

    // 이벤트 리스너 등록
    // addEventListener가 없는 구버전 브라우저 대응
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
    } else {
      // deprecated but fallback for older browsers
      mediaQuery.addListener(handleChange);
    }

    // 클린업 함수
    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handleChange);
      } else {
        mediaQuery.removeListener(handleChange);
      }
    };
  }, [query]);

  return matches;
};

/**
 * 사전 정의된 브레이크포인트 훅들
 * theme.js의 브레이크포인트를 기반으로 만든 편의 훅들
 */

// 모바일 디바이스 감지 (768px 미만)
export const useIsMobile = () => {
  return useMediaQuery('(max-width: 767px)');
};

// 태블릿 디바이스 감지 (768px ~ 1023px)
export const useIsTablet = () => {
  return useMediaQuery('(min-width: 768px) and (max-width: 1023px)');
};

// 데스크톱 디바이스 감지 (1024px 이상)
export const useIsDesktop = () => {
  return useMediaQuery('(min-width: 1024px)');
};

// 큰 데스크톱 감지 (1350px 이상)
export const useIsLargeDesktop = () => {
  return useMediaQuery('(min-width: 1350px)');
};

// 터치 디바이스 감지
export const useIsTouchDevice = () => {
  return useMediaQuery('(hover: none) and (pointer: coarse)');
};

// Hover 가능한 디바이스 감지 (마우스가 있는 디바이스)
export const useCanHover = () => {
  return useMediaQuery('(hover: hover) and (pointer: fine)');
};

export default useMediaQuery;
